<script setup lang="ts">
import { ref } from "vue";
import { useToast } from "@/composable/useToast";
import { useTranslation } from "i18next-vue";
import type { FileParams, FileType } from "@/types/file.types";
import DraggableUploader from "./DraggableUploader.vue";
import { uploadFilesBatch } from "@/utils/upload-files.utils";
import { compressImages } from "@/utils/image-compression.utils";
import { filter } from "lodash";
import LoadingText from "./skeleton/LoadingText.vue";

const props = withDefaults(
  defineProps<{
    fileType: FileType;
    fileParams: FileParams;
    maxFiles?: number;
    maxSizeMb?: number;
    extensions?: string[];
    description?: (v: { maxSizeMb: number; maxFiles: number }) => string;
    imagesOnly?: boolean;
    multiple?: boolean;
  }>(),
  {
    maxFiles: 100,
    maxSizeMb: 20,
    imagesOnly: false,
    extensions: () => [],
    multiple: true,
  }
);

const emit = defineEmits<{
  (e: "uploaded"): void;
}>();

const { t } = useTranslation();
const { showDanger, showLoading } = useToast();

const uploadingFiles = ref<
  {
    file: File;
    progress: number;
  }[]
>([]);

function beforeUpload(file: File) {
  if (props.imagesOnly && !file.type.startsWith("image/")) {
    showDanger("toast:upload.images-only");
    return false;
  }
  const isLtMax = file.size / 1024 / 1024 < props.maxSizeMb;
  if (!isLtMax) {
    showDanger("toast:upload.size-exceeded", { maxSizeMb: props.maxSizeMb });
    return false;
  }
  return true;
}

function filterFiles(files: File[]) {
  return filter(files, (file) => beforeUpload(file));
}

async function upload(files: File[]) {
  try {
    const items = files.map((file) => ({
      file,
      fileType: props.fileType,
      params: props.fileParams,
    }));
    await uploadFilesBatch(items, (index: number, percent: number) => {
      const uploadingFile = uploadingFiles.value[index];
      if (uploadingFile) {
        uploadingFile.progress = percent;
      }
    });
  } catch {
    showDanger("toast:upload.error");
  }
}

const isCompressing = ref(false);

const handleNewFiles = async (filesList: File[]) => {
  if (filesList.length > props.maxFiles) {
    showDanger("toast:upload.max-files-exceeded", { maxFiles: props.maxFiles });
    return;
  }

  const filteredFiles = filterFiles(filesList);
  if (filteredFiles.length === 0) {
    return;
  }

  // Compress images before upload (only for image files)
  let filesToUpload = filteredFiles;
  if (props.imagesOnly) {
    isCompressing.value = true;
    try {
      filesToUpload = await compressImages(filteredFiles, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
        maxSizeBytes: 1024 * 1024, // 1MB target
      });
    } catch(e) {
      console.warn("Image compression failed, uploading originals:", e);
    }
    isCompressing.value = false;
  }

  uploadingFiles.value = [...filesToUpload].map((file) => ({
    file,
    progress: 0,
  }));
  const loadingNotif = showLoading(t("uploadProgressDialog.uploadInProgress"));
  upload(filesToUpload).then(() => {
    uploadingFiles.value = [];
    loadingNotif.close();
    emit("uploaded");
  });
};
</script>

<template>
  <!-- Compressing state -->
  <div v-if="isCompressing" class="px-3 pb-4">
    <div
      class="d-flex justify-content-center align-items-center gap-2 py-4 rounded bg-light text-dark"
    >
      <el-icon class="is-loading" size="20"
        ><i class="bi-arrow-repeat"></i
      ></el-icon>
      <span>{{ $t("uploadProgressDialog.compressing") }}</span>
    </div>
  </div>

  <DraggableUploader
    v-else-if="!uploadingFiles?.length"
    :description="description ? description({ maxSizeMb, maxFiles }) : ''"
    :extensions="extensions"
    :multiple="multiple"
    @files="handleNewFiles"
  />

  <div v-else class="px-3 pb-4">
    <div
      class="d-flex justify-content-center py-2 mb-3 rounded bg-warning-light text-dark"
    >
      <LoadingText :text="$t('uploadProgressDialog.waitBeforeClose')" />
    </div>

    <div class="upload-list-scroll rounded bordered">
      <div class="p-3">
        <div
          v-for="({ file, progress }, index) in uploadingFiles"
          :key="index"
          class="mt-2"
        >
          <div class="row rounded shadow-1 py-1">
            <div class="col-6 d-flex align-items-center gap-2">
              <el-icon size="1rem"
                ><i
                  :class="
                    file.type.startsWith('image/')
                      ? 'bi-image'
                      : 'bi-file-earmark'
                  "
                ></i
              ></el-icon>
              <div class="text-truncate" style="font-size: 12px">
                {{ file.name }}
              </div>
            </div>
            <el-progress
              class="col-6"
              :percentage="progress || 0"
              :status="progress >= 100 ? 'success' : undefined"
              :stroke-width="8"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.el-upload-dragger {
  background-color: var(--bs-lighter) !important;
}

.upload-list-scroll {
  max-height: 50vh;
  overflow-y: scroll;
  overflow-x: hidden;
}
</style>
